import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faArrowUpRightFromSquare,
	faChartPie,
	faMagnifyingGlassPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { Icon } from "@/components/Icon/Icon";

import { KpiSection } from "./KpiSection";
import { formatNumber, formatPercent } from "../utils/percentage";
import { AnalyticsSegments, KpiBreakdownBucket, SegmentDimension } from "../schema/analytics";
import { CLICKABLE_KPI_KEYS, KPI_TO_METRIC_FIELD, KpiKey } from "../utils/kpiFilterMap";

export interface KpiBreakdownProps {
	segments: AnalyticsSegments;
	onBucketOpenInContacts: (
		dimension: SegmentDimension,
		bucket: KpiBreakdownBucket,
		kpiKey: KpiKey
	) => void;
	onBucketDrillDown: (dimension: SegmentDimension, bucket: KpiBreakdownBucket) => void;
}

const DIMENSIONS: SegmentDimension[] = ["seniority", "jobTitle", "geo", "industry"];

const hasAnyNonZeroMetric = (bucket: KpiBreakdownBucket): boolean => {
	if (!bucket || typeof bucket !== "object" || !bucket.metrics) {
		return false;
	}

	return CLICKABLE_KPI_KEYS.some((kpiKey) => {
		const metricField = KPI_TO_METRIC_FIELD[kpiKey];

		return (bucket.metrics[metricField] ?? 0) > 0;
	});
};

const isBucketLike = (value: unknown): value is KpiBreakdownBucket => {
	return (
		!!value &&
		typeof value === "object" &&
		!Array.isArray(value) &&
		"metrics" in (value as Record<string, unknown>)
	);
};

const toBucketArray = (value: unknown): KpiBreakdownBucket[] => {
	if (Array.isArray(value)) {
		return value as KpiBreakdownBucket[];
	}

	if (value && typeof value === "object") {
		// Flat record of buckets keyed by id, e.g. { DE: { key, label, metrics } }.
		return Object.values(value as Record<string, unknown>).filter(isBucketLike);
	}

	return [];
};

// Resolve the raw bucket list for a UI dimension from the segments payload.
// `seniority` and `jobTitle` live inside the nested `role` object returned by
// the backend, while `geo` and `industry` are top-level fields.
const resolveDimensionBuckets = (
	segments: AnalyticsSegments,
	dimension: SegmentDimension
): KpiBreakdownBucket[] => {
	if (dimension === "seniority" || dimension === "jobTitle") {
		const role = segments.role;

		if (role && typeof role === "object" && !Array.isArray(role)) {
			const sub = (role as Record<string, unknown>)[dimension];

			if (sub !== undefined) {
				return toBucketArray(sub);
			}

			// Legacy flat record of bucket-like values (no seniority/jobTitle keys).
			// Expose under seniority only.
			if (dimension === "seniority") {
				return toBucketArray(role);
			}

			return [];
		}

		// Legacy array shape: expose under seniority only.
		if (Array.isArray(role) && dimension === "seniority") {
			return toBucketArray(role);
		}

		return [];
	}

	return toBucketArray(segments[dimension]);
};

const filterNonEmptyBuckets = (
	segments: AnalyticsSegments,
	dimension: SegmentDimension
): KpiBreakdownBucket[] => {
	return resolveDimensionBuckets(segments, dimension).filter(hasAnyNonZeroMetric);
};

const getFirstAvailableDimension = (segments: AnalyticsSegments): SegmentDimension | null => {
	for (const dimension of DIMENSIONS) {
		if (filterNonEmptyBuckets(segments, dimension).length > 0) {
			return dimension;
		}
	}

	return null;
};

export const KpiBreakdown: FunctionComponent<KpiBreakdownProps> = ({
	segments,
	onBucketOpenInContacts,
	onBucketDrillDown,
}) => {
	const { t } = useTranslation();
	const initialDimension = useMemo(() => getFirstAvailableDimension(segments), [segments]);
	const [activeDimension, setActiveDimension] = useState<SegmentDimension | null>(initialDimension);
	const [activeKpi, setActiveKpi] = useState<KpiKey>(CLICKABLE_KPI_KEYS[0]);

	const effectiveDimension = activeDimension ?? initialDimension;
	const buckets: KpiBreakdownBucket[] = effectiveDimension
		? filterNonEmptyBuckets(segments, effectiveDimension)
		: [];

	const metricField = KPI_TO_METRIC_FIELD[activeKpi];
	const sortedBuckets = useMemo(() => {
		return [...buckets].sort(
			(a, b) => (b.metrics[metricField] ?? 0) - (a.metrics[metricField] ?? 0)
		);
	}, [buckets, metricField]);

	const dimensionTotal = sortedBuckets.reduce(
		(sum, bucket) => sum + (bucket.metrics[metricField] ?? 0),
		0
	);
	const maxValue = sortedBuckets.reduce(
		(max, bucket) => Math.max(max, bucket.metrics[metricField] ?? 0),
		0
	);

	const hasAnyData = DIMENSIONS.some((dim) => filterNonEmptyBuckets(segments, dim).length > 0);

	if (!hasAnyData) {
		return null;
	}

	return (
		<KpiSection
			icon={faChartPie}
			subtitle={t("analytics.breakdownSubtitle")}
			title={t("analytics.breakdownTitle")}
		>
			<div className="flex flex-row flex-wrap gap-2 items-center mb-1" role="tablist">
				<span className="text-xs text-text-meta uppercase tracking-wide mr-1">
					{t("analytics.breakdownDimensionLabel")}
				</span>
				{DIMENSIONS.map((dimension) => {
					const dimensionBuckets = filterNonEmptyBuckets(segments, dimension);
					const isDisabled = dimensionBuckets.length === 0;
					const isActive = effectiveDimension === dimension;

					return (
						<button
							key={dimension}
							aria-selected={isActive}
							className={classNames(
								"text-sm px-3 py-1 rounded-xl border transition-colors",
								isActive
									? "bg-blue-action text-brand-white border-blue-action"
									: "bg-brand-white text-text-meta border-border-light hover:bg-blue-action-200",
								isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
							)}
							disabled={isDisabled}
							role="tab"
							type="button"
							onClick={() => setActiveDimension(dimension)}
						>
							{t(`analytics.breakdownBy.${dimension}`)}
						</button>
					);
				})}
			</div>

			<div className="flex flex-row flex-wrap gap-2 items-center mb-2">
				<span className="text-xs text-text-meta uppercase tracking-wide mr-1">
					{t("analytics.breakdownKpiLabel")}
				</span>
				{CLICKABLE_KPI_KEYS.map((kpiKey) => {
					const isActive = activeKpi === kpiKey;

					return (
						<button
							key={kpiKey}
							aria-pressed={isActive}
							className={classNames(
								"text-sm px-3 py-1 rounded-xl border transition-colors cursor-pointer",
								isActive
									? "bg-text text-brand-white border-text"
									: "bg-brand-white text-text-meta border-border-light hover:bg-blue-action-200"
							)}
							type="button"
							onClick={() => setActiveKpi(kpiKey)}
						>
							{t(`analytics.kpiLabel.${kpiKey}`)}
						</button>
					);
				})}
			</div>

			{effectiveDimension && sortedBuckets.length > 0 ? (
				<div className="flex flex-col gap-1.5">
					{sortedBuckets.map((bucket) => {
						const value = bucket.metrics[metricField] ?? 0;
						const pctOfTotal = dimensionTotal > 0 ? (value / dimensionTotal) * 100 : 0;
						const relToMax = maxValue > 0 ? (value / maxValue) * 100 : 0;
						const isActionable = bucket.key !== null && bucket.key !== "";
						const isZero = value === 0;
						const showActions = isActionable && !isZero;

						return (
							<div
								key={`${effectiveDimension}-${bucket.key ?? "unknown"}-${bucket.label}`}
								aria-label={`${bucket.label}: ${formatNumber(value)}`}
								className={classNames(
									"group grid grid-cols-[160px_1fr_80px_72px_auto] gap-3 items-center w-full rounded-md px-2 py-2 transition-colors",
									showActions ? "hover:bg-blue-action-100" : "",
									isZero ? "opacity-50" : ""
								)}
								role="group"
							>
								<div className="text-sm text-text font-medium truncate">{bucket.label}</div>
								<div className="h-3 bg-brand-white border border-border-light rounded-full overflow-hidden">
									<div
										className="h-full bg-blue-action-200 transition-all duration-300"
										style={{ width: `${Math.min(100, Math.max(0, relToMax))}%` }}
									/>
								</div>
								<div className="text-sm text-text-meta text-right tabular-nums">
									{formatPercent(pctOfTotal, 1)}
								</div>
								<div className="text-sm text-text text-right tabular-nums font-semibold">
									{formatNumber(value)}
								</div>
								<div
									className={classNames(
										"flex flex-row items-center gap-1 transition-opacity",
										showActions
											? "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
											: "invisible"
									)}
								>
									<button
										aria-label={t("analytics.drillDownTo", { label: bucket.label })}
										className="flex items-center justify-center w-7 h-7 rounded-md text-text-meta hover:bg-blue-action hover:text-brand-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-action"
										disabled={!showActions}
										title={t("analytics.drillDown")}
										type="button"
										onClick={() => onBucketDrillDown(effectiveDimension, bucket)}
									>
										<Icon className="w-3.5 h-3.5" icon={faMagnifyingGlassPlus} />
									</button>
									<button
										aria-label={t("analytics.openInContactsFor", { label: bucket.label })}
										className="flex items-center justify-center w-7 h-7 rounded-md text-text-meta hover:bg-blue-action hover:text-brand-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-action"
										disabled={!showActions}
										title={t("analytics.openInContacts")}
										type="button"
										onClick={() => onBucketOpenInContacts(effectiveDimension, bucket, activeKpi)}
									>
										<Icon className="w-3.5 h-3.5" icon={faArrowUpRightFromSquare} />
									</button>
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="text-sm text-text-meta py-4">{t("analytics.breakdownNoData")}</div>
			)}
		</KpiSection>
	);
};
